<template>
  <Modal
    :model-value="modelValue"
    :title="$t('upsell.multiLangTitle')"
    @update:model-value="$emit('update:modelValue', $event)"
  >
    <p class="upsell-lead">{{ $t('upsell.multiLangBody') }}</p>
    <!-- Fluency-only: Foundation does not grant multiLingual, so offering it
         here would take payment without unlocking the add-language gate. -->
    <UpgradeOptions
      :current-plan-id="currentPlanId"
      :user-country="userCountry"
      :current-provider="currentProvider"
      :on-upgrade="handleUpgradeRequest"
      :fluency-only="true"
      @iap-purchase-complete="handleIapPurchaseComplete"
    />
  </Modal>
</template>

<script>
import { ref as vueRef, watch, onUnmounted } from "vue";
import { getDatabase, ref, onValue } from "firebase/database";
import Modal from "@/components/Modal.vue";
import UpgradeOptions from "@/components/UpgradeOptions.vue";
import { authStore } from "@/store/auth";
import { pricingStore } from "@/store/pricing.js";
import { stripeUtils } from "@/scripts/stripeUtils.js";
import { platformBilling } from "@/scripts/platformBilling.js";
import { useToast } from "@/composables/useToast.js";
import i18n from "@/locales";

export default {
  name: "MultiLanguageUpsellModal",
  components: { Modal, UpgradeOptions },
  props: {
    modelValue: {
      type: Boolean,
      default: false,
    },
  },
  emits: ["update:modelValue"],
  setup(props, { emit }) {
    const { showToast } = useToast();
    const currentPlanId = vueRef("free");
    const currentProvider = vueRef(null);
    const userCountry = vueRef("US");

    // The listener follows authStore.userId: at mount the uid may not have
    // resolved yet, and it changes again on sign-out/sign-in.
    let unsubscribe = null;
    const stopUserWatch = watch(
      () => authStore.userId,
      (userId) => {
        if (unsubscribe) {
          unsubscribe();
          unsubscribe = null;
        }
        currentPlanId.value = "free";
        currentProvider.value = null;
        userCountry.value = "US";
        if (!userId) return;
        unsubscribe = onValue(
          ref(getDatabase(), `users/${userId}`),
          (snapshot) => {
            const data = snapshot.val() || {};
            // Respects billing.status, so a canceled subscription resolves to free.
            const plan = pricingStore.getCurrentPlan(data.billing);
            currentPlanId.value = plan.id;
            // null when free; fall back to "stripe" for legacy paid rows that
            // predate the provider field (matches AccountView / FreeChatView).
            currentProvider.value =
              plan.id === "free" ? null : data?.billing?.provider || "stripe";
            userCountry.value = platformBilling.getUserCountry(data);
            // Stripe grants arrive via webhook while this modal is open (the
            // IAP path closes through iap-purchase-complete). Once the plan
            // is Fluency there is nothing left to sell — close so the user
            // lands back on the switcher with add-language now unlocked.
            if (plan.id === "fluency" && props.modelValue) {
              emit("update:modelValue", false);
            }
          },
          (error) => {
            console.error("Upsell modal user read failed:", error);
          }
        );
      },
      { immediate: true }
    );
    onUnmounted(() => {
      if (unsubscribe) unsubscribe();
      stopUserWatch();
    });

    const handleUpgradeRequest = async (planType) => {
      const [planName, billingCycleName] = planType.split("-");
      const plan = pricingStore.plans[planName];
      const lookupKey =
        billingCycleName === "month" ? plan.monthlyLookupKey : plan.yearlyLookupKey;
      try {
        await stripeUtils.startCheckout(lookupKey);
      } catch (error) {
        console.error("Error creating upgrade checkout session:", error);
        showToast(i18n.global.t("errors.billing.upgradeStartFailed"), "error");
      }
    };

    const handleIapPurchaseComplete = () => {
      // Purchase succeeded; the new Fluency features arrive via the user
      // subscription, so just close the upsell.
      emit("update:modelValue", false);
    };

    return {
      currentPlanId,
      currentProvider,
      userCountry,
      handleUpgradeRequest,
      handleIapPurchaseComplete,
    };
  },
};
</script>

<style scoped>
.upsell-lead {
  color: var(--al-text);
  margin-bottom: 1.25rem;
  line-height: 1.5;
}
</style>
